import React, { useEffect, useState } from "react";
import { Button, Modal, Typography } from "antd";
import { BatchJob } from "reducers/types";

interface Props {
  job: BatchJob | null;
  providerName: string;
  onApprove: () => void;
  onCancel: () => void;
}

const formatCountdown = (ms: number) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
};

// Opens while the job awaits approval (provider down, U15). Closing it only hides it for this pause;
// the card's JobWaitBanner keeps the same two actions. The server auto-cancels at approvalDeadline (U17).
const JobApprovalModal: React.FC<Props> = ({ job, providerName, onApprove, onCancel }) => {
  const [now, setNow] = useState(Date.now());
  const [dismissedFor, setDismissedFor] = useState<string | null>(null);

  const awaiting = !!job && job.status === "AWAITING_APPROVAL";
  const pauseKey = job ? `${job.jobId}:${job.approvalDeadline || ""}` : null;

  useEffect(() => {
    if (!awaiting) {
      return;
    }
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [awaiting]);

  if (!job || !awaiting || dismissedFor === pauseKey) {
    return null;
  }

  const remaining = job.approvalDeadline ? job.approvalDeadline - now : 0;

  return (
    <Modal
      visible
      title={`${providerName} is unavailable`}
      onCancel={() => setDismissedFor(pauseKey)}
      footer={[
        <Button key="cancel" danger onClick={onCancel}>
          Cancel job
        </Button>,
        <Button key="approve" type="primary" onClick={onApprove}>
          Run when available
        </Button>,
      ]}
    >
      {job.waitReason ? <Typography.Paragraph type="secondary">{job.waitReason}</Typography.Paragraph> : null}
      <Typography.Paragraph>
        Run this job automatically when {providerName} is available again? It is checked every hour.
      </Typography.Paragraph>
      <Typography.Text type="warning">
        {remaining > 0 ? `Auto-cancels in ${formatCountdown(remaining)}` : "Cancelling…"}
      </Typography.Text>
    </Modal>
  );
};

export default JobApprovalModal;
